/**
 * Company Accent Colors
 *
 * Defines 6 preset accent colors that admins can apply company-wide.
 * Each color includes light and dark mode CSS variable overrides for accessibility.
 * Blue is the default (no overrides needed since it's the base theme).
 * Admins can also pick a custom hex color; dark mode overrides are derived automatically.
 *
 * All preset colors are chosen to meet WCAG AA contrast ratios:
 * - Text colors: ≥4.5:1 against their respective backgrounds
 * - Button backgrounds: ≥4.5:1 with white text
 * - Button backgrounds: >3:1 with background surface they sit on
 */
import chroma from 'chroma-js';

import { generateAlphaScale, generateColorScale } from './theme-generator';

export type CompanyAccentColorId = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'violet' | 'custom';

export interface CompanyAccentColor {
	id: CompanyAccentColorId;
	name: string;
	swatch: string;
	light: Record<string, string>;
	dark: Record<string, string>;
}

const primaryOverrides = (opts: {
	fg: string;
	fgStrong: string;
	fgStronger: string;
	fgStrongest: string;
	bgAlpha: string;
	bgAlphaHover: string;
	bgAlphaActive: string;
	bg: string;
	bgHover: string;
	bgActive: string;
	interactive: string;
	focus: string;
}): Record<string, string> => ({
	'--chakra-colors-primary-fg': opts.fg,
	'--chakra-colors-primary-fgStrong': opts.fgStrong,
	'--chakra-colors-primary-fgStronger': opts.fgStronger,
	'--chakra-colors-primary-fgStrongest': opts.fgStrongest,
	'--chakra-colors-primary-bgAlpha': opts.bgAlpha,
	'--chakra-colors-primary-bgAlphaHover': opts.bgAlphaHover,
	'--chakra-colors-primary-bgAlphaActive': opts.bgAlphaActive,
	'--chakra-colors-primary-bg': opts.bg,
	'--chakra-colors-primary-bgHover': opts.bgHover,
	'--chakra-colors-primary-bgActive': opts.bgActive,
	'--chakra-colors-text-interactive': opts.interactive,
	'--chakra-colors-border-focus': opts.focus,
});

export const COMPANY_ACCENT_COLORS: CompanyAccentColor[] = [
	{
		id: 'red',
		name: 'Red',
		swatch: '#d82c2f',
		light: primaryOverrides({
			fg: '#d82c2fff',
			fgStrong: '#ad2527ff',
			fgStronger: '#832421ff',
			fgStrongest: '#5e1b17ff',
			bgAlpha: '#d82c2f14',
			bgAlphaHover: '#d82c2f29',
			bgAlphaActive: '#d82c2f3d',
			bg: '#d82c2fff',
			bgHover: '#ad2527ff',
			bgActive: '#832421ff',
			interactive: '#d82c2fff',
			focus: '#d82c2fff',
		}),
		dark: primaryOverrides({
			fg: '#fbeaeaff',
			fgStrong: '#ee7c7cff',
			fgStronger: '#efaaa9ff',
			fgStrongest: '#ee7c7cff',
			bgAlpha: '#d82c2f29',
			bgAlphaHover: '#d82c2f3d',
			bgAlphaActive: '#d82c2f3d',
			bg: '#d82c2fff',
			bgHover: '#ad2527ff',
			bgActive: '#832421ff',
			interactive: '#ee7c7cff',
			focus: '#d82c2fff',
		}),
	},
	{
		id: 'orange',
		name: 'Orange',
		swatch: '#b44a0b',
		light: primaryOverrides({
			fg: '#b44a0bff',
			fgStrong: '#8a3605ff',
			fgStronger: '#762c00ff',
			fgStrongest: '#642700ff',
			bgAlpha: '#ff650714',
			bgAlphaHover: '#ff650729',
			bgAlphaActive: '#ff65073d',
			bg: '#b44a0bff',
			bgHover: '#8a3605ff',
			bgActive: '#762c00ff',
			interactive: '#b44a0bff',
			focus: '#b44a0bff',
		}),
		dark: primaryOverrides({
			fg: '#ffe3baff',
			fgStrong: '#ff8b1bff',
			fgStronger: '#ffaa51ff',
			fgStrongest: '#ff8b1bff',
			bgAlpha: '#ff650729',
			bgAlphaHover: '#ff65073d',
			bgAlphaActive: '#ff65073d',
			bg: '#b44a0bff',
			bgHover: '#8a3605ff',
			bgActive: '#762c00ff',
			interactive: '#ff8b1bff',
			focus: '#b44a0bff',
		}),
	},
	{
		id: 'yellow',
		name: 'Yellow',
		swatch: '#916402',
		light: primaryOverrides({
			fg: '#916402ff',
			fgStrong: '#5e4003ff',
			fgStronger: '#4d3505ff',
			fgStrongest: '#382705ff',
			bgAlpha: '#986b0114',
			bgAlphaHover: '#986b0129',
			bgAlphaActive: '#986b013d',
			bg: '#916402ff',
			bgHover: '#5e4003ff',
			bgActive: '#4d3505ff',
			interactive: '#916402ff',
			focus: '#916402ff',
		}),
		dark: primaryOverrides({
			fg: '#faf0c7ff',
			fgStrong: '#e6ae14ff',
			fgStronger: '#fcc84fff',
			fgStrongest: '#e6ae14ff',
			bgAlpha: '#986b0129',
			bgAlphaHover: '#986b013d',
			bgAlphaActive: '#986b013d',
			bg: '#b37e00ff',
			bgHover: '#916402ff',
			bgActive: '#5e4003ff',
			interactive: '#e6ae14ff',
			focus: '#b37e00ff',
		}),
	},
	{
		id: 'green',
		name: 'Green',
		swatch: '#118342',
		light: primaryOverrides({
			fg: '#118342ff',
			fgStrong: '#066a35ff',
			fgStronger: '#055229ff',
			fgStrongest: '#073b21ff',
			bgAlpha: '#11834214',
			bgAlphaHover: '#11834229',
			bgAlphaActive: '#1183423d',
			bg: '#118342ff',
			bgHover: '#066a35ff',
			bgActive: '#055229ff',
			interactive: '#118342ff',
			focus: '#118342ff',
		}),
		dark: primaryOverrides({
			fg: '#d9f2e3ff',
			fgStrong: '#4bb475ff',
			fgStronger: '#7ccb9bff',
			fgStrongest: '#4bb475ff',
			bgAlpha: '#11834229',
			bgAlphaHover: '#1183423d',
			bgAlphaActive: '#1183423d',
			bg: '#118342ff',
			bgHover: '#066a35ff',
			bgActive: '#055229ff',
			interactive: '#4bb475ff',
			focus: '#118342ff',
		}),
	},
	{
		id: 'blue',
		name: 'Blue',
		swatch: '#1670df',
		light: {},
		dark: primaryOverrides({
			fg: '#d8ecf8ff',
			fgStrong: '#5ba1f1ff',
			fgStronger: '#8fbcf0ff',
			fgStrongest: '#5ba1f1ff',
			bgAlpha: '#1670df29',
			bgAlphaHover: '#1670df3d',
			bgAlphaActive: '#1670df3d',
			bg: '#1670dfff',
			bgHover: '#1358b9ff',
			bgActive: '#14458aff',
			interactive: '#5ba1f1ff',
			focus: '#1670dfff',
		}),
	},
	{
		id: 'violet',
		name: 'Violet',
		swatch: '#7b5ce0',
		light: primaryOverrides({
			fg: '#7b5ce0ff',
			fgStrong: '#6940ceff',
			fgStronger: '#5036a6ff',
			fgStrongest: '#3a2579ff',
			bgAlpha: '#7b5ce014',
			bgAlphaHover: '#7b5ce01f',
			bgAlphaActive: '#7b5ce033',
			bg: '#7b5ce0ff',
			bgHover: '#6940ceff',
			bgActive: '#5036a6ff',
			interactive: '#7b5ce0ff',
			focus: '#7b5ce0ff',
		}),
		dark: primaryOverrides({
			fg: '#efedfcff',
			fgStrong: '#9c8ef1ff',
			fgStronger: '#bab2f5ff',
			fgStrongest: '#9c8ef1ff',
			bgAlpha: '#7b5ce01f',
			bgAlphaHover: '#7b5ce033',
			bgAlphaActive: '#7b5ce033',
			bg: '#7b5ce0ff',
			bgHover: '#6940ceff',
			bgActive: '#5036a6ff',
			interactive: '#9c8ef1ff',
			focus: '#7b5ce0ff',
		}),
	},
];

export const DEFAULT_ACCENT_COLOR_ID: CompanyAccentColorId = 'blue';

export const getCompanyAccentColorById = (id: CompanyAccentColorId): CompanyAccentColor | undefined => COMPANY_ACCENT_COLORS.find((c) => c.id === id);

export const getCompanyAccentColorBySwatch = (hex: string): CompanyAccentColor | undefined =>
	COMPANY_ACCENT_COLORS.find((c) => c.swatch.toLowerCase() === hex.toLowerCase());

/**
 * Resolve a hex color to a CompanyAccentColor.
 * Returns the hand-tuned preset if the hex matches a preset swatch,
 * otherwise generates one from the hex. Returns null for null/undefined input.
 */
export const resolveAccentColorFromHex = (hex: string | null | undefined): CompanyAccentColor | null => {
	if (!hex) return null;

	const preset = getCompanyAccentColorBySwatch(hex);
	if (preset) return preset;

	try {
		return generateCompanyAccentFromHex(hex);
	} catch (e) {
		console.warn('[resolveAccentColorFromHex] Invalid hex:', hex, e);
		return null;
	}
};

/**
 * Resolve a hex color from the API into a preset color ID and optional custom hex.
 * Returns the default if hex is null/undefined.
 */
export const resolveHexToPresetId = (hex: string | null | undefined): { colorId: CompanyAccentColorId; customHex: string | null } => {
	if (!hex) return { colorId: DEFAULT_ACCENT_COLOR_ID, customHex: null };

	const preset = getCompanyAccentColorBySwatch(hex);
	if (preset) return { colorId: preset.id, customHex: null };

	return { colorId: 'custom', customHex: hex };
};

const getTokenValue = (overrides: Record<string, string>, key: string, fallback: string): string => overrides[`--chakra-colors-${key}`] ?? fallback;

/**
 * Convert a CompanyAccentColor into a Chakra semantic token override object
 * suitable for extendTheme(). Returns null for the default blue preset.
 */
export const toSemanticTokenOverrides = (color: CompanyAccentColor) => {
	if (color.id === DEFAULT_ACCENT_COLOR_ID && Object.keys(color.light).length === 0) return null;

	const s = color.swatch;

	return {
		colors: {
			primary: {
				fg: { default: getTokenValue(color.light, 'primary-fg', s), _dark: getTokenValue(color.dark, 'primary-fg', s) },
				fgStrong: { default: getTokenValue(color.light, 'primary-fgStrong', s), _dark: getTokenValue(color.dark, 'primary-fgStrong', s) },
				fgStronger: { default: getTokenValue(color.light, 'primary-fgStronger', s), _dark: getTokenValue(color.dark, 'primary-fgStronger', s) },
				fgStrongest: { default: getTokenValue(color.light, 'primary-fgStrongest', s), _dark: getTokenValue(color.dark, 'primary-fgStrongest', s) },
				bgAlpha: { default: getTokenValue(color.light, 'primary-bgAlpha', s), _dark: getTokenValue(color.dark, 'primary-bgAlpha', s) },
				bgAlphaHover: { default: getTokenValue(color.light, 'primary-bgAlphaHover', s), _dark: getTokenValue(color.dark, 'primary-bgAlphaHover', s) },
				bgAlphaActive: { default: getTokenValue(color.light, 'primary-bgAlphaActive', s), _dark: getTokenValue(color.dark, 'primary-bgAlphaActive', s) },
				bg: { default: getTokenValue(color.light, 'primary-bg', s), _dark: getTokenValue(color.dark, 'primary-bg', s) },
				bgHover: { default: getTokenValue(color.light, 'primary-bgHover', s), _dark: getTokenValue(color.dark, 'primary-bgHover', s) },
				bgActive: { default: getTokenValue(color.light, 'primary-bgActive', s), _dark: getTokenValue(color.dark, 'primary-bgActive', s) },
			},
			text: {
				interactive: { default: getTokenValue(color.light, 'text-interactive', s), _dark: getTokenValue(color.dark, 'text-interactive', s) },
			},
			border: {
				focus: { default: getTokenValue(color.light, 'border-focus', s), _dark: getTokenValue(color.dark, 'border-focus', s) },
			},
		},
	};
};

/**
 * Generate a CompanyAccentColor from an arbitrary hex color.
 * Light mode uses the chosen color directly. Dark mode derives lighter
 * tints using LAB lightness so they remain legible on dark backgrounds.
 */
export const generateCompanyAccentFromHex = (hex: string): CompanyAccentColor => {
	const base = chroma(hex);
	const scale = generateColorScale(hex);
	const alphaScale = generateAlphaScale(hex);

	const lightness = base.get('lab.l');

	// Dark-mode foreground tints: push toward high lightness while keeping hue
	const darkFg = base.set('lab.l', Math.min(95, lightness + 40)).hex();
	const darkFgStrong = base.set('lab.l', Math.min(75, lightness + 15)).hex();
	const darkFgStronger = base.set('lab.l', Math.min(85, lightness + 25)).hex();
	const darkInteractive = darkFgStrong;

	const light = primaryOverrides({
		fg: scale['500'],
		fgStrong: scale['600'],
		fgStronger: scale['700'],
		fgStrongest: scale['800'],
		bgAlpha: alphaScale['100'],
		bgAlphaHover: alphaScale['200'],
		bgAlphaActive: alphaScale['300'],
		bg: scale['500'],
		bgHover: scale['600'],
		bgActive: scale['700'],
		interactive: scale['500'],
		focus: scale['500'],
	});

	const dark = primaryOverrides({
		fg: darkFg,
		fgStrong: darkFgStrong,
		fgStronger: darkFgStronger,
		fgStrongest: darkFgStrong,
		bgAlpha: alphaScale['200'],
		bgAlphaHover: alphaScale['300'],
		bgAlphaActive: alphaScale['300'],
		bg: scale['500'],
		bgHover: scale['600'],
		bgActive: scale['700'],
		interactive: darkInteractive,
		focus: scale['500'],
	});

	return {
		id: 'custom',
		name: 'Custom',
		swatch: hex,
		light,
		dark,
	};
};
